import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

const STEPS = { UPLOAD: 1, PREVIEW: 2, RESULT: 3, DONE: 4 };

export default function CustomerImportTab({ t }) {
  const [step, setStep] = useState(STEPS.UPLOAD);
  const [importFile, setImportFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  // Preview data
  const [preview, setPreview] = useState(null);

  // Import result
  const [result, setResult] = useState(null);

  // Email sending
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  // SMS sending
  const [testPhone, setTestPhone] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null);

  // Test account modal
  const [showTestModal, setShowTestModal] = useState(false);
  const [testForm, setTestForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [testAccount, setTestAccount] = useState(null); // { id, name } of created test account
  const [deletingTest, setDeletingTest] = useState(false);

  // Past batches
  const [batches, setBatches] = useState([]);
  const [showBatches, setShowBatches] = useState(false);

  useEffect(() => {
    if (showBatches) loadBatches();
  }, [showBatches]);

  async function loadBatches() {
    try {
      const res = await axios.get(`${API}/admin/customers/batches`);
      setBatches(res.data);
    } catch { /* ignore */ }
  }

  function reset() {
    setStep(STEPS.UPLOAD);
    setImportFile(null);
    setPreview(null);
    setResult(null);
    setEmailResult(null);
    setSmsResult(null);
    setTestPhone('');
    setShowTestModal(false);
    setTestForm({ first_name: '', last_name: '', email: '', phone: '' });
    setTestAccount(null);
    setMsg({ type: '', text: '' });
  }

  async function handlePreview() {
    if (!importFile) return;
    setLoading(true);
    setMsg({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await axios.post(`${API}/admin/customers/import?preview=true`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview(res.data);
      setStep(STEPS.PREVIEW);
    } catch (err) {
      setMsg({ type: 'danger', text: err.response?.data?.error || 'Preview failed' });
    } finally {
      setLoading(false);
    }
  }

  async function handleImport() {
    if (!importFile) return;
    setLoading(true);
    setMsg({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await axios.post(`${API}/admin/customers/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      setStep(STEPS.RESULT);
      setMsg({ type: 'success', text: `${res.data.imported} customers imported, ${res.data.updated} updated, ${res.data.skipped} skipped` });
    } catch (err) {
      setMsg({ type: 'danger', text: err.response?.data?.error || 'Import failed' });
    } finally {
      setLoading(false);
    }
  }

  async function handleSendWelcome(isTest) {
    if (!result?.batchId) return;
    setEmailSending(true);
    setMsg({ type: '', text: '' });
    try {
      const payload = { batch_id: result.batchId };
      if (isTest) {
        payload.test_email = testForm.email.trim();
        payload.test_first_name = testForm.first_name.trim();
        payload.test_last_name = testForm.last_name.trim();
        if (testForm.phone.trim()) payload.test_phone = testForm.phone.trim();
      }
      const res = await axios.post(`${API}/admin/customers/send-welcome`, payload);
      setEmailResult(res.data);
      if (isTest) {
        if (res.data.testUserId) {
          setTestAccount({ id: res.data.testUserId, name: res.data.testUserName });
        }
        setShowTestModal(false);
        setMsg({ type: 'success', text: `Test account created and welcome email sent to ${testForm.email}` });
      } else {
        // Update progress in result
        setResult(prev => prev ? { ...prev, emailsSent: (prev.emailsSent || 0) + (res.data.sent || 0), emailsFailed: (prev.emailsFailed || 0) + (res.data.failed || 0) } : prev);
        setMsg({ type: 'success', text: `${res.data.sent} welcome emails sent` + (res.data.failed > 0 ? ` (${res.data.failed} failed)` : '') + (res.data.skippedTokens > 0 ? ` (${res.data.skippedTokens} already had tokens)` : '') + (res.data.quotaExhausted ? ' — Daily quota reached, try again tomorrow.' : '') });
      }
    } catch (err) {
      setMsg({ type: 'danger', text: err.response?.data?.error || 'Failed to send emails' });
    } finally {
      setEmailSending(false);
    }
  }

  async function handleDeleteTestAccount() {
    if (!testAccount?.id) return;
    setDeletingTest(true);
    try {
      await axios.delete(`${API}/admin/customers/test-account/${testAccount.id}`);
      setMsg({ type: 'success', text: `Test account "${testAccount.name}" deleted` });
      setTestAccount(null);
    } catch (err) {
      setMsg({ type: 'danger', text: err.response?.data?.error || 'Failed to delete test account' });
    } finally {
      setDeletingTest(false);
    }
  }

  async function handleSendSms(isTest) {
    if (!result?.batchId) return;
    setSmsSending(true);
    setMsg({ type: '', text: '' });
    try {
      const payload = { batch_id: result.batchId };
      if (isTest && testPhone.trim()) payload.test_phone = testPhone.trim();
      const res = await axios.post(`${API}/admin/customers/send-sms`, payload);
      setSmsResult(res.data);
      if (isTest) {
        setMsg({ type: 'success', text: `Test SMS sent to ${testPhone}` });
      } else {
        setResult(prev => prev ? { ...prev, smsSent: (prev.smsSent || 0) + (res.data.sent || 0), smsFailed: (prev.smsFailed || 0) + (res.data.failed || 0) } : prev);
        setMsg({ type: 'success', text: `${res.data.sent} SMS sent` + (res.data.failed > 0 ? ` (${res.data.failed} failed)` : '') });
      }
    } catch (err) {
      setMsg({ type: 'danger', text: err.response?.data?.error || 'Failed to send SMS' });
    } finally {
      setSmsSending(false);
    }
  }

  const cardStyle = { background: '#151e2d', border: '1px solid rgba(255,255,255,0.1)' };
  const labelColor = { color: '#94A3B8' };
  const statNum = (color) => ({ fontSize: '1.5rem', color, fontWeight: 600 });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h5 style={{ color: '#E8ECF0', margin: 0 }}>{t.admin?.import?.title || 'Import Customers'}</h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowBatches(!showBatches)}>
          {showBatches ? 'Hide History' : 'Import History'}
        </button>
      </div>

      {msg.text && (
        <div className={`alert alert-${msg.type} alert-dismissible`} role="alert">
          {msg.text}
          <button type="button" className="btn-close" onClick={() => setMsg({ type: '', text: '' })}></button>
        </div>
      )}

      {/* ── Past Batches ── */}
      {showBatches && (
        <div className="card mb-3" style={cardStyle}>
          <div className="card-body">
            <h6 style={{ color: '#E8ECF0', marginBottom: '1rem' }}>Import History</h6>
            {batches.length === 0 ? (
              <p style={labelColor}>No imports yet.</p>
            ) : (
              <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                <table className="table table-dark table-sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Source</th>
                      <th>File</th>
                      <th>Imported</th>
                      <th>Emails</th>
                      <th>SMS</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map(b => (
                      <tr key={b.id}>
                        <td>{new Date(b.created_at).toLocaleDateString()}</td>
                        <td>{b.source}</td>
                        <td style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.source_file || '—'}</td>
                        <td>{b.imported_count}</td>
                        <td>{b.emails_sent}/{b.email_eligible}</td>
                        <td>{b.sms_sent}/{b.sms_eligible}</td>
                        <td>
                          <span className={`badge bg-${b.status === 'completed' ? 'success' : b.status === 'importing' ? 'warning' : 'secondary'}`}>
                            {b.status}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {b.status === 'completed' && (b.emails_sent < b.email_eligible || b.sms_sent < b.sms_eligible) && (
                            <button className="btn btn-outline-primary btn-sm" style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                              onClick={() => {
                                setResult({ batchId: b.id, emailEligible: b.email_eligible, imported: b.imported_count, phoneOnlyCount: b.sms_eligible, updated: 0, skipped: 0, emailsSent: b.emails_sent || 0, emailsFailed: b.emails_failed || 0, smsSent: b.sms_sent || 0, smsFailed: b.sms_failed || 0 });
                                setEmailResult(null);
                                setSmsResult(null);
                                setStep(STEPS.RESULT);
                                setShowBatches(false);
                              }}>
                              {b.emails_sent < b.email_eligible && b.sms_sent < b.sms_eligible
                                ? 'Send Emails & SMS'
                                : b.emails_sent < b.email_eligible ? 'Send Emails' : 'Send SMS'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Step 1: Upload ── */}
      {step === STEPS.UPLOAD && (
        <div className="card" style={cardStyle}>
          <div className="card-body">
            <p style={{ ...labelColor, fontSize: '0.9rem' }}>
              Upload a QuickBooks export (.xlsx) or CSV file to import customers.
              XLSX files are auto-parsed — business names are filtered out, duplicates are merged, and phone numbers are cleaned.
              Customers with email get accounts created + welcome emails. Phone-only customers receive an SMS invitation instead.
            </p>
            <div className="mb-3">
              <input type="file" accept=".csv,.xlsx,.xls" className="form-control form-control-sm"
                onChange={e => setImportFile(e.target.files[0])} />
            </div>
            {importFile && (
              <div className="mb-3" style={{ ...labelColor, fontSize: '0.85rem' }}>
                Selected: <strong style={{ color: '#E8ECF0' }}>{importFile.name}</strong>
                ({(importFile.size / 1024).toFixed(1)} KB)
                {/\.xlsx?$/i.test(importFile.name) && (
                  <span className="badge bg-info ms-2" style={{ fontSize: '0.7rem' }}>XLSX</span>
                )}
                {/\.csv$/i.test(importFile.name) && (
                  <span className="badge bg-secondary ms-2" style={{ fontSize: '0.7rem' }}>CSV</span>
                )}
              </div>
            )}
            <button className="btn btn-primary btn-sm" disabled={!importFile || loading} onClick={handlePreview}>
              {loading ? 'Analyzing...' : 'Preview Import'}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Preview ── */}
      {step === STEPS.PREVIEW && preview && (
        <div>
          <div className="card mb-3" style={cardStyle}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 style={{ color: '#E8ECF0', margin: 0 }}>Import Preview</h6>
                {preview.format && (
                  <span className="badge bg-info">{preview.format.toUpperCase()} Format</span>
                )}
              </div>

              {/* Stats grid */}
              <div className="row g-2 mb-3">
                <div className="col-6 col-md-3 text-center">
                  <div style={statNum('#E8ECF0')}>{preview.totalRows}</div>
                  <small style={labelColor}>Total Rows</small>
                </div>
                <div className="col-6 col-md-3 text-center">
                  <div style={statNum('#22c55e')}>{preview.toImport}</div>
                  <small style={labelColor}>New Customers</small>
                </div>
                <div className="col-6 col-md-3 text-center">
                  <div style={statNum('#ffb870')}>{preview.toSkip}</div>
                  <small style={labelColor}>Already Exist</small>
                </div>
                <div className="col-6 col-md-3 text-center">
                  <div style={statNum('#60a5fa')}>{preview.emailEligible}</div>
                  <small style={labelColor}>Email (Account)</small>
                </div>
              </div>

              {/* Phone-only + XLSX-specific stats */}
              <div className="row g-2 mb-3">
                {preview.phoneOnlyCount > 0 && (
                  <div className="col-6 col-md-3 text-center">
                    <div style={statNum('#a78bfa')}>{preview.phoneOnlyCount}</div>
                    <small style={labelColor}>Phone Only (SMS)</small>
                  </div>
                )}
                {preview.format === 'xlsx' && preview.skippedBusinesses > 0 && (
                  <div className="col-6 col-md-3 text-center">
                    <div style={statNum('#D42B2B')}>{preview.skippedBusinesses}</div>
                    <small style={labelColor}>Businesses Filtered</small>
                  </div>
                )}
                {preview.format === 'xlsx' && preview.mergeGroups > 0 && (
                  <div className="col-6 col-md-3 text-center">
                    <div style={statNum('#94A3B8')}>{preview.mergeGroups}</div>
                    <small style={labelColor}>Duplicates Merged</small>
                  </div>
                )}
              </div>

              {/* Customer preview table */}
              {preview.customers && preview.customers.length > 0 && (
                <div className="table-responsive mb-3" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  <table className="table table-dark table-sm" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.customers.map((c, i) => (
                        <tr key={i}>
                          <td>{c.first_name} {c.last_name}</td>
                          <td>{c.email || <span style={{ color: '#666' }}>—</span>}</td>
                          <td>{c.phone || <span style={{ color: '#666' }}>—</span>}</td>
                          <td>
                            {c.email ? (
                              <span className="badge bg-primary" style={{ fontSize: '0.65rem' }}>Account + Email</span>
                            ) : c.phone ? (
                              <span className="badge bg-info" style={{ fontSize: '0.65rem' }}>SMS Only</span>
                            ) : (
                              <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>Skip</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.customers.length >= 50 && (
                    <small style={labelColor}>Showing first 50 of {preview.toImport} customers</small>
                  )}
                </div>
              )}

              {/* Skipped business names */}
              {preview.skippedRows && preview.skippedRows.length > 0 && (
                <details className="mb-3">
                  <summary style={{ ...labelColor, cursor: 'pointer', fontSize: '0.85rem' }}>
                    Filtered business names ({preview.skippedBusinesses})
                  </summary>
                  <ul style={{ color: '#d07070', fontSize: '0.8rem', maxHeight: '100px', overflowY: 'auto', marginTop: '0.5rem' }}>
                    {preview.skippedRows.map((s, i) => (
                      <li key={i}>Row {s.row}: {s.client}</li>
                    ))}
                  </ul>
                </details>
              )}

              {/* CSV errors */}
              {preview.errors && preview.errors.length > 0 && (
                <details className="mb-3">
                  <summary style={{ color: '#D42B2B', cursor: 'pointer', fontSize: '0.85rem' }}>
                    Errors ({preview.errors.length})
                  </summary>
                  <ul style={{ color: '#d07070', fontSize: '0.8rem', maxHeight: '100px', overflowY: 'auto', marginTop: '0.5rem' }}>
                    {preview.errors.map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
                  </ul>
                </details>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-success btn-sm" disabled={loading || preview.toImport === 0} onClick={handleImport}>
                  {loading ? 'Importing...' : `Import ${preview.emailEligible} Customers`}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={reset}>
                  Cancel
                </button>
              </div>
              {preview.phoneOnlyCount > 0 && (
                <small style={{ ...labelColor, display: 'block', marginTop: '0.5rem', fontSize: '0.8rem' }}>
                  {preview.phoneOnlyCount} phone-only customers will not get accounts — they'll receive an SMS invitation after import.
                </small>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Step 3: Import Result ── */}
      {step === STEPS.RESULT && result && (
        <div>
          <div className="card mb-3" style={cardStyle}>
            <div className="card-body">
              <h6 style={{ color: '#E8ECF0', marginBottom: '1rem' }}>Import Complete</h6>

              <div className="row g-2 mb-3">
                <div className="col-3 text-center">
                  <div style={statNum('#22c55e')}>{result.imported}</div>
                  <small style={labelColor}>Imported</small>
                </div>
                <div className="col-3 text-center">
                  <div style={statNum('#60a5fa')}>{result.updated}</div>
                  <small style={labelColor}>Updated</small>
                </div>
                <div className="col-3 text-center">
                  <div style={statNum('#ffb870')}>{result.skipped}</div>
                  <small style={labelColor}>Skipped</small>
                </div>
                <div className="col-3 text-center">
                  <div style={statNum('#a78bfa')}>{result.phoneOnlyCount || 0}</div>
                  <small style={labelColor}>Phone Only</small>
                </div>
              </div>

              {result.batchId && (
                <p style={{ ...labelColor, fontSize: '0.8rem', marginBottom: '1rem' }}>
                  Batch ID: <code style={{ color: '#a78bfa' }}>{result.batchId}</code>
                </p>
              )}

              {/* ── Welcome Email Section ── */}
              {result.emailEligible > 0 && (() => {
                const sent = result.emailsSent || 0;
                const failed = result.emailsFailed || 0;
                const total = result.emailEligible;
                const remaining = Math.max(0, total - sent - failed);
                const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
                const allDone = remaining === 0;
                return (
                <div className="card mb-3" style={{ background: '#1a2538', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className="card-body">
                    <h6 style={{ color: '#E8ECF0', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                      Welcome Emails {allDone && <span className="badge bg-success ms-2" style={{ fontSize: '0.7rem' }}>Complete</span>}
                    </h6>

                    {/* Progress stats */}
                    <div className="row g-2 mb-3">
                      <div className="col-3 text-center">
                        <div style={statNum('#22c55e')}>{sent}</div>
                        <small style={labelColor}>Sent</small>
                      </div>
                      <div className="col-3 text-center">
                        <div style={statNum('#D42B2B')}>{failed}</div>
                        <small style={labelColor}>Failed</small>
                      </div>
                      <div className="col-3 text-center">
                        <div style={statNum('#60a5fa')}>{remaining}</div>
                        <small style={labelColor}>Remaining</small>
                      </div>
                      <div className="col-3 text-center">
                        <div style={statNum('#E8ECF0')}>{total}</div>
                        <small style={labelColor}>Total</small>
                      </div>
                    </div>

                    {/* Progress bar */}
                    {sent > 0 && (
                      <div style={{ background: '#0C1220', borderRadius: '4px', height: '8px', marginBottom: '1rem', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: allDone ? '#22c55e' : '#60a5fa', transition: 'width 0.3s' }} />
                      </div>
                    )}

                    {!allDone && (
                      <>
                        <p style={{ ...labelColor, fontSize: '0.85rem' }}>
                          {sent === 0
                            ? `${total} customers can receive a welcome email with a 10% discount and password setup link.`
                            : `${remaining} customers still need to receive their welcome email.`}
                        </p>

                        {/* Test email button */}
                        <div className="mb-3">
                          <button className="btn btn-outline-info btn-sm" onClick={() => setShowTestModal(true)}>
                            Send Test Email (creates a test account)
                          </button>
                        </div>

                        {/* Test account info */}
                        {testAccount && (
                          <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: '0.85rem' }}>
                            Test account <strong>{testAccount.name}</strong> created. Check your email, click the link, set a password, and log in to verify the full flow.
                            <button className="btn btn-outline-danger btn-sm ms-2" style={{ fontSize: '0.75rem', padding: '1px 8px' }}
                              disabled={deletingTest} onClick={handleDeleteTestAccount}>
                              {deletingTest ? '...' : 'Delete Test Account'}
                            </button>
                          </div>
                        )}

                        {/* Send remaining */}
                        <button className="btn btn-primary btn-sm" disabled={emailSending}
                          onClick={() => handleSendWelcome(false)}>
                          {emailSending ? 'Sending...' : `Send Welcome Emails to ${remaining} Customers`}
                        </button>
                      </>
                    )}

                    {/* Email result from last send */}
                    {emailResult && !emailResult.testUserId && (
                      <div className="mt-3 p-2" style={{ background: '#0C1220', borderRadius: '6px' }}>
                        <p style={{ ...labelColor, fontSize: '0.8rem', marginBottom: '0.5rem' }}>Last send result:</p>
                        <div className="row g-2">
                          <div className="col-4 text-center">
                            <div style={statNum('#22c55e')}>{emailResult.sent}</div>
                            <small style={labelColor}>Sent</small>
                          </div>
                          <div className="col-4 text-center">
                            <div style={statNum('#D42B2B')}>{emailResult.failed}</div>
                            <small style={labelColor}>Failed</small>
                          </div>
                          <div className="col-4 text-center">
                            <div style={statNum('#ffb870')}>{emailResult.skippedTokens || 0}</div>
                            <small style={labelColor}>Already Sent</small>
                          </div>
                        </div>
                        {emailResult.quotaExhausted && (
                          <div className="alert alert-warning mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
                            Daily email quota reached. {emailResult.remaining} emails remaining — try again tomorrow.
                          </div>
                        )}
                        {emailResult.errors && emailResult.errors.length > 0 && (
                          <details className="mt-2">
                            <summary style={{ color: '#D42B2B', cursor: 'pointer', fontSize: '0.8rem' }}>
                              Failed emails ({emailResult.errors.length})
                            </summary>
                            <ul style={{ color: '#d07070', fontSize: '0.75rem', maxHeight: '80px', overflowY: 'auto', marginTop: '0.25rem' }}>
                              {emailResult.errors.map((e, i) => <li key={i}>{e.email}: {e.reason}</li>)}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                );
              })()}

              {/* ── SMS Invitation Section (Phone-Only Customers) ── */}
              {(result.phoneOnlyCount || 0) > 0 && (() => {
                const sent = result.smsSent || 0;
                const failed = result.smsFailed || 0;
                const total = result.phoneOnlyCount;
                const remaining = Math.max(0, total - sent - failed);
                const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
                const allDone = remaining === 0;
                return (
                <div className="card mb-3" style={{ background: '#1a2538', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className="card-body">
                    <h6 style={{ color: '#E8ECF0', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                      SMS Invitations {allDone && <span className="badge bg-success ms-2" style={{ fontSize: '0.7rem' }}>Complete</span>}
                    </h6>

                    {/* Progress stats */}
                    <div className="row g-2 mb-3">
                      <div className="col-3 text-center">
                        <div style={statNum('#22c55e')}>{sent}</div>
                        <small style={labelColor}>Sent</small>
                      </div>
                      <div className="col-3 text-center">
                        <div style={statNum('#D42B2B')}>{failed}</div>
                        <small style={labelColor}>Failed</small>
                      </div>
                      <div className="col-3 text-center">
                        <div style={statNum('#60a5fa')}>{remaining}</div>
                        <small style={labelColor}>Remaining</small>
                      </div>
                      <div className="col-3 text-center">
                        <div style={statNum('#E8ECF0')}>{total}</div>
                        <small style={labelColor}>Total</small>
                      </div>
                    </div>

                    {/* Progress bar */}
                    {sent > 0 && (
                      <div style={{ background: '#0C1220', borderRadius: '4px', height: '8px', marginBottom: '1rem', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: allDone ? '#22c55e' : '#60a5fa', transition: 'width 0.3s' }} />
                      </div>
                    )}

                    {!allDone && (
                      <>
                        <p style={{ ...labelColor, fontSize: '0.85rem' }}>
                          {sent === 0
                            ? `${total} customers have phone numbers only (no email). Send them a bilingual SMS inviting them to create an account with a 10% discount.`
                            : `${remaining} customers still need to receive their SMS invitation.`}
                        </p>

                        {/* Test SMS */}
                        <div className="mb-3">
                          <label style={{ ...labelColor, fontSize: '0.8rem', marginBottom: '4px', display: 'block' }}>
                            Test first (send to your phone):
                          </label>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <input type="tel" className="form-control form-control-sm" style={{ maxWidth: '220px' }}
                              placeholder="+1 514 123 4567" value={testPhone} onChange={e => setTestPhone(e.target.value)} />
                            <button className="btn btn-outline-primary btn-sm" disabled={!testPhone.trim() || smsSending}
                              onClick={() => handleSendSms(true)}>
                              {smsSending ? '...' : 'Send Test SMS'}
                            </button>
                          </div>
                        </div>

                        {/* Send remaining SMS */}
                        <button className="btn btn-primary btn-sm" disabled={smsSending}
                          onClick={() => handleSendSms(false)}>
                          {smsSending ? 'Sending...' : `Send SMS to ${remaining} Customers`}
                        </button>
                      </>
                    )}

                    {/* SMS result from last send */}
                    {smsResult && (
                      <div className="mt-3 p-2" style={{ background: '#0C1220', borderRadius: '6px' }}>
                        <p style={{ ...labelColor, fontSize: '0.8rem', marginBottom: '0.5rem' }}>Last send result:</p>
                        <div className="row g-2">
                          <div className="col-6 text-center">
                            <div style={statNum('#22c55e')}>{smsResult.sent}</div>
                            <small style={labelColor}>Sent</small>
                          </div>
                          <div className="col-6 text-center">
                            <div style={statNum('#D42B2B')}>{smsResult.failed}</div>
                            <small style={labelColor}>Failed</small>
                          </div>
                        </div>
                        {smsResult.errors && smsResult.errors.length > 0 && (
                          <details className="mt-2">
                            <summary style={{ color: '#D42B2B', cursor: 'pointer', fontSize: '0.8rem' }}>
                              Failed SMS ({smsResult.errors.length})
                            </summary>
                            <ul style={{ color: '#d07070', fontSize: '0.75rem', maxHeight: '80px', overflowY: 'auto', marginTop: '0.25rem' }}>
                              {smsResult.errors.map((e, i) => <li key={i}>{e.phone}: {e.reason}</li>)}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                );
              })()}

              <button className="btn btn-outline-secondary btn-sm" onClick={() => setStep(STEPS.DONE)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 4: All Done ── */}
      {step === STEPS.DONE && (
        <div className="card" style={cardStyle}>
          <div className="card-body text-center">
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>&#10003;</div>
            <h6 style={{ color: '#E8ECF0', marginBottom: '0.5rem' }}>All Done</h6>
            <p style={{ ...labelColor, fontSize: '0.9rem' }}>
              {result?.imported || 0} customers imported
              {emailResult?.sent > 0 ? `, ${emailResult.sent} welcome emails sent` : ''}
              {smsResult?.sent > 0 ? `, ${smsResult.sent} SMS invitations sent` : ''}.
            </p>
            <button className="btn btn-outline-secondary btn-sm" onClick={reset}>
              Import Another File
            </button>
          </div>
        </div>
      )}

      {/* ── Test Account Modal ── */}
      {showTestModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowTestModal(false)}>
          <div style={{ background: '#1a2538', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '1.5rem', width: '100%', maxWidth: '420px', margin: '1rem' }}
            onClick={e => e.stopPropagation()}>
            <h6 style={{ color: '#E8ECF0', marginBottom: '1rem' }}>Create Test Account</h6>
            <p style={{ ...labelColor, fontSize: '0.85rem', marginBottom: '1rem' }}>
              This creates a real customer account and sends the welcome email. You can test the full flow (email, activation link, password setup, login) then delete the account after.
            </p>
            <div className="mb-2">
              <label style={{ ...labelColor, fontSize: '0.8rem' }}>First Name</label>
              <input type="text" className="form-control form-control-sm" placeholder="Jean"
                value={testForm.first_name} onChange={e => setTestForm(f => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="mb-2">
              <label style={{ ...labelColor, fontSize: '0.8rem' }}>Last Name</label>
              <input type="text" className="form-control form-control-sm" placeholder="Dupont"
                value={testForm.last_name} onChange={e => setTestForm(f => ({ ...f, last_name: e.target.value }))} />
            </div>
            <div className="mb-2">
              <label style={{ ...labelColor, fontSize: '0.8rem' }}>Email (where to send the test)</label>
              <input type="email" className="form-control form-control-sm" placeholder="your@email.com"
                value={testForm.email} onChange={e => setTestForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="mb-3">
              <label style={{ ...labelColor, fontSize: '0.8rem' }}>Phone <span style={{ opacity: 0.5 }}>(optional)</span></label>
              <input type="tel" className="form-control form-control-sm" placeholder="+1 514 123 4567"
                value={testForm.phone} onChange={e => setTestForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowTestModal(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm"
                disabled={!testForm.email.trim() || !testForm.first_name.trim() || !testForm.last_name.trim() || emailSending}
                onClick={() => handleSendWelcome(true)}>
                {emailSending ? 'Creating...' : 'Create & Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
